import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Button, IconButton, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { ConditionList } from "./condition-list";
import { SegmentAudience } from "./segment-audience";
import { SegmentPreview } from "./segment-preview";
import { segmentGroupHasErrors } from "./utils/errors";

import type { NoNullFields } from "../../../../server/supabase/base-supabase-service";
import type { ChannelName } from "../../../channels/types";
import type { UsableAccountPersonField } from "../../server/supabase-segment-field-service";
import type { Segment, SegmentMode } from "../../types/segment-schema";
import type { SegmentManagerAction, SegmentReducerState } from "./reducer/actions";
import type { Dispatch, FC } from "react";

interface SegmentGroupProps {
  segment: Segment["segment_json"]["segments"][number];
  segmentIndex: number;
  dispatch: Dispatch<SegmentManagerAction<Segment>>;
  segmentFields: NoNullFields<UsableAccountPersonField>[];
  canDelete: boolean;
  errors: SegmentReducerState<Segment>["errors"];
  channels: ChannelName[];
  disabled: boolean;
  mode: SegmentMode;
  showPreview: boolean;
}

export const SegmentGroup: FC<SegmentGroupProps> = ({
  segment,
  segmentIndex,
  dispatch,
  segmentFields,
  canDelete,
  errors,
  channels,
  disabled,
  mode,
  showPreview,
}) => {
  const hasErrors = useMemo(
    () => segmentGroupHasErrors(errors, segmentIndex),
    [errors, segmentIndex],
  );

  return (
    <Paper sx={{ p: 2, backgroundColor: "background.default" }}>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom>
            {`Group ${segmentIndex + 1}`}
          </Typography>
          {canDelete && (
            <IconButton
              aria-label="delete-group"
              onClick={() => dispatch({ type: "REMOVE_GROUP", payload: segmentIndex })}
              data-analytics-id="delete-segment-group"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography>Select</Typography>
          <SegmentAudience
            segment={segment}
            segmentFields={segmentFields}
            handleFieldChange={(value) =>
              dispatch({
                type: "UPDATE_GROUP_AUDIENCE",
                payload: { index: segmentIndex, recipient_type_key: value },
              })
            }
            disabled={disabled}
            errMsg={
              errors.display
                ? errors.errors?.properties?.segment_json?.properties?.segments?.items?.[
                    segmentIndex
                  ]?.properties?.recipient_type_key?.errors?.join(", ")
                : undefined
            }
            mode={mode}
          />
          <Typography>that match</Typography>
          <Select
            id="root-operation"
            size="small"
            value={segment.operation}
            disabled={disabled}
            data-testid="operationHint"
            onChange={(e) =>
              dispatch({
                type: "UPDATE_GROUP_OPERATION",
                payload: { index: segmentIndex, operation: e.target.value },
              })
            }
          >
            <MenuItem value="AND"> all </MenuItem>
            <MenuItem value="OR"> any </MenuItem>
          </Select>

          <Typography>the following criteria</Typography>
        </Stack>

        <Stack
          direction="column"
          spacing={2}
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            p: 4,
            borderRadius: 1,
            borderColor: "divider",
            borderWidth: 1,
            borderStyle: "solid",
          }}
        >
          <ConditionList
            segment={segment}
            segmentIndex={segmentIndex}
            dispatch={dispatch}
            segmentFields={segmentFields}
            errors={errors}
            disabled={disabled}
            recipientTypeKey={segment.recipient_type_key ?? undefined}
          />

          {!disabled && (
            <Button
              variant="text"
              onClick={() =>
                dispatch({ type: "ADD_CONDITION", payload: { groupIndex: segmentIndex } })
              }
              data-analytics-id="add-condition-button"
              startIcon={<AddIcon />}
              sx={{ maxWidth: "fit-content" }}
              disabled={disabled}
            >
              {segment.conditions.length === 0
                ? "Add a condition"
                : segment.operation === "AND"
                  ? "And ..."
                  : "Or ..."}
            </Button>
          )}
        </Stack>
        {!hasErrors && segment.conditions.length > 0 && showPreview && (
          <SegmentPreview segment={segment} channels={channels} type="group" />
        )}
      </Stack>
    </Paper>
  );
};
